import { useEffect, useMemo, useState } from "react"
import { useToggle } from "react-use"
import cn from "classnames"

import { Box, Button, Img, Input, Text, TextWithIcon } from "ui-kit/components-branded/index"
import { createCalendarShareLink } from "modules/ClientAccount/utils"
import { useGetClientCalendarLink, useScrollByUrlHash } from "modules/ClientAccount/hooks"
import { Analytics } from "utils/analytics"
import { notificationCopyUrl } from "utils/helpers"
import style from "modules/ClientAccount/components/GetCalendarLinkWidget/style.module.scss"

export function GetCalendarLinkWidget() {
    useScrollByUrlHash()
    const { data: calendarData } = useGetClientCalendarLink()
    const [shareLink, setShareLink] = useState<string | undefined>(undefined)
    const [isCreating, toggleCreating] = useToggle(false)
    const isLinkGenerated = useMemo(() => Boolean(calendarData), [calendarData])

    useEffect(() => {
        if (!isLinkGenerated) return
        setShareLink(calendarData?.share_link)
    }, [isLinkGenerated])

    function handleClickCreateLinkButton() {
        toggleCreating(true)
        Analytics.calendar_add_settings()
        createCalendarShareLink()
            .then(({ share_link }) => {
                setShareLink(share_link)
            })
            .catch(() => undefined)
            .finally(() => {
                toggleCreating(false)
            })
    }

    function handleClickCopyLinkButton() {
        if (shareLink) {
            navigator.clipboard
                .writeText(shareLink)
                .then(() => {
                    notificationCopyUrl()
                })
                .catch(() => undefined)
        }
    }

    return (
        <Box>
            <div className="is-flex mgb-4" id="attach-calendar">
                <TextWithIcon
                    icon={
                        <Img className={cn(style.emoji, "flex-shrink-0")} src="calendar-jule.png" />
                    }
                    position="left"
                    isAccent
                    size="h3"
                    className={style.title}
                >
                    Добавьте сессии себе в&nbsp;календарь
                </TextWithIcon>
            </div>
            <Text className="is-block mgb-3">
                {!shareLink && <>Создайте ссылку и&nbsp;добавьте ее&nbsp;в&nbsp;календарь.</>}
                {shareLink && <>Скопируйте ссылку и&nbsp;добавьте ее&nbsp;в&nbsp;календарь.</>}
            </Text>
            <hr className="mgb-3" />
            <Text className="is-block">Google Календарь</Text>
            <Text className={cn(style.description, "is-block", "mgb-4")}>
                Меню &laquo;Другие календари&raquo;&nbsp;&mdash; &laquo;Добавить по&nbsp;URL&raquo;
            </Text>
            <Text className="is-block">Apple Календарь</Text>
            <Text className={cn(style.description, "is-block", "mgb-3")}>
                Меню &laquo;Файл&raquo;&nbsp;&mdash; &laquo;Подписка на&nbsp;новый
                календарь...&raquo;
            </Text>
            {!shareLink && (
                <Button
                    isFullwidth
                    color="primary"
                    onClick={handleClickCreateLinkButton}
                    disabled={isLinkGenerated}
                    isLoading={isCreating}
                >
                    Создать ссылку
                </Button>
            )}
            {shareLink && (
                <div className="is-flex">
                    <div className="flex-grow-1 mgr-4">
                        <Input className={style.input} defaultValue={shareLink} />
                    </div>
                    <Button color="primary" onClick={handleClickCopyLinkButton}>
                        Скопировать
                    </Button>
                </div>
            )}
        </Box>
    )
}
